import { faFlag, faHeart } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  FamilyChildProfile,
  JoonUIColor,
  SegmentedControl,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"

import { useDoterChatStore } from "./useDoterChatStore"
import NotificationDot2 from "../../../components/notificationDot/NotificationDot2"
import { PREFERENCE_KEY } from "../../../constants"
import useMobile from "../../../hooks/useMobile"
import { useFlaggedDoterChatLogsQuery } from "../../../networking/chatSessions/queries"
import { useUpdatePreferenceMutation } from "../../../networking/families/mutations"
import { FlagType } from "../../../types"
import useFamilyUserSelectStore from "../hooks/useFamilyUserSelect"
import useSelectedFamilyMember from "../hooks/useSelectedFamilyMember"
import { DoterAIInfoStep1 } from "../manageGameplay/doterChat/DoterAIInfoModal"
import FlaggedConvos from "../manageGameplay/doterChat/FlaggedConvos"

const DoterChatTab = () => {
  const { tab, onOpen: onOpenDoterChatModal } = useDoterChatStore()
  const { selectedProfile } = useSelectedFamilyMember()
  const { t } = useTranslation()
  const hasDoterChatEnabled = (
    selectedProfile as FamilyChildProfile | undefined
  )?.user.preferences.enable_doter_chat

  if (!hasDoterChatEnabled) {
    return <DoterChatDeactivatedState />
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="bg-white rounded-lg p-1 flex flex-row justify-start items-center">
        <TextButton onClick={onOpenDoterChatModal}>
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.text.primaryAccent}
          >
            {t("DC_VIEW_ALL_MESSAGES")}
          </Typography>
        </TextButton>
      </div>
      <DoterChatTabNav />
      <FlaggedConvos type={tab} />
    </div>
  )
}

const DoterChatTabNav = () => {
  const { tab, setTab } = useDoterChatStore()
  const { selectedUserId } = useFamilyUserSelectStore()
  const { t } = useTranslation()
  const { data: redFlaggedConvos } = useFlaggedDoterChatLogsQuery(FlagType.RED)
  const { data: greenFlaggedConvos } = useFlaggedDoterChatLogsQuery(
    FlagType.GREEN
  )
  const filteredRedFlaggedConvos = redFlaggedConvos?.filter(
    (convo) => convo.user_id === selectedUserId
  )
  const filteredGreenFlaggedConvos = greenFlaggedConvos?.filter(
    (convo) => convo.user_id === selectedUserId
  )

  const unreadRedConvos = filteredRedFlaggedConvos?.filter(
    (convo) => !convo.date_read
  )
  const unreadGreenConvos = filteredGreenFlaggedConvos?.filter(
    (convo) => !convo.date_read
  )

  return (
    <SegmentedControl
      value={tab}
      setValue={(value) => setTab(value as FlagType)}
      options={[
        {
          label: (
            <div className="flex flex-row items-center gap-2 justify-center">
              <FontAwesomeIcon icon={faFlag} />
              <Typography variant="bodySmall">
                {t("DC_NEEDS_ATTENTION")}
              </Typography>
              {unreadRedConvos && unreadRedConvos?.length > 0 && (
                <NotificationDot2 />
              )}
            </div>
          ),
          value: FlagType.RED,
        },
        {
          label: (
            <div className="flex flex-row items-center gap-2 justify-center">
              <FontAwesomeIcon icon={faHeart} />
              <Typography variant="bodySmall">
                {t("DC_POSITIVE_MOMENTS")}
              </Typography>
              {unreadGreenConvos && unreadGreenConvos?.length > 0 && (
                <NotificationDot2 />
              )}
            </div>
          ),
          value: FlagType.GREEN,
        },
      ]}
      fullWidth
    />
  )
}

const DoterChatDeactivatedState = () => {
  const { selectedProfile } = useSelectedFamilyMember()
  const updatePreferenceMutation = useUpdatePreferenceMutation()
  const isMobile = useMobile()
  const { t } = useTranslation()

  const handleTurnOnDoterChat = async () => {
    if (!selectedProfile) return
    const childId = selectedProfile?.user.id

    updatePreferenceMutation.mutateAsync({
      childId: childId,
      key: PREFERENCE_KEY.enableDoterChat,
      value: true,
    })
  }
  return (
    <div className="relative max-w-[600px] mx-auto">
      <DoterAIInfoStep1 isEmptyState={true} />
      <div
        className={`sticky pt-4 pb-2 ${isMobile ? "bottom-[88px]" : "bottom-[40px]"} w-full left-0 bg-gradient-to-b from-transparent via-white/80 to-transparent backdrop-blur-[0.5px]`}
      >
        <div className="max-w-[600px] mx-auto px-4">
          <Button
            text={t("DC_TURN_ON_FOR", { name: selectedProfile?.user.name })}
            onClick={handleTurnOnDoterChat}
            isLoading={updatePreferenceMutation.isLoading}
            fullWidth
          />
        </div>
      </div>
    </div>
  )
}

export default DoterChatTab
